import axios from "axios"
import { API_URL } from "../utils/ApiLinks"
import type { Contact, ContactDto } from "../types/crm/types/contact"


interface CacheItem<T> {
  data: T
  timestamp: number
  key: string
}

class SimpleCache {
  private cache: Record<string, CacheItem<any>> = {}
  private readonly TTL: number = 60000

  set<T>(key: string, data: T): void {
    this.cache[key] = {
      data,
      timestamp: Date.now(),
      key,
    }
  }

  get<T>(key: string): T | null {
    const item = this.cache[key]
    if (!item) return null

    if (Date.now() - item.timestamp > this.TTL) {
      delete this.cache[key]
      return null
    }

    return item.data as T
  }

  invalidate(key: string): void {
    delete this.cache[key]
  }

  invalidateAll(): void {
    this.cache = {}
  }
}

const cache = new SimpleCache()

class ContactStore {
  private contacts: Record<number, ContactDto> = {}

  storeContacts(contactsList: ContactDto[]): void {
    contactsList.forEach((contact) => {
      this.contacts[contact.id] = contact
    })
  }

  getContactById(id: number): ContactDto | null {
    return this.contacts[id] || null
  }

  hasContact(id: number): boolean {
    return !!this.contacts[id]
  }

  clear(): void {
    this.contacts = {}
  }
}

const contactStore = new ContactStore()

const getUserContacts = async (userProfileId: number): Promise<ContactDto[]> => {

  try {
    const url = `${API_URL}/contact/get-user-contacts/${userProfileId}`

    const response = await axios.get<ContactDto[]>(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
    })

    contactStore.storeContacts(response.data)

    return response.data
  } catch (error: any) {
    console.error("Error en getUserContacts:", error)
    console.error("Error detallado:", {
      message: error.message,
      response: error.response,
      request: error.request,
      config: error.config,
    })
    handleRequestError(error)
    return []
  }
}


const getContactById = async (contactId: number): Promise<ContactDto | null> => {

  try {
    const url = `${API_URL}/contact/get-contact-by-id/${contactId}`

    const response = await axios.get<ContactDto>(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
    })
    return response.data
  } catch (error: any) {
    console.error("Error en getContactById:", error)
    console.error("Error detallado:", {
      message: error.message,
      response: error.response,
      request: error.request,
      config: error.config,
    })
    handleRequestError(error)
    return null
  }
}

const createContact = async (formData: FormData): Promise<void> => {
  try {
    const url = `${API_URL}/contact/create-contact`;

    const response = await axios.post(url, formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Error en createContact:", error);
    console.error("Detalles:", {
      message: error.message,
      response: error.response,
      request: error.request,
      config: error.config,
    });
    handleRequestError(error);
    throw new Error(error.response?.data || "Error al crear contacto");
  }
};



const updateContact = async (
  contactId: number,
  updateData: Contact,
): Promise<{ success: boolean; message: string }> => {
  try {
    const url = `${API_URL}/contact/update-contact/${contactId}`

    console.log(updateData)

    const response = await axios.put(url, updateData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        "Content-Type": "application/json",
      },
    })

    const success = response.status >= 200 && response.status < 300
    const message = response.data || "Contacto actualizada con éxito"

    contactStore.clear()
    cache.invalidateAll()

    return {
      success,
      message,
    }
  } catch (error: any) {
    console.error("Error en updateContact:", error)
    console.error("Error detallado:", {
      message: error.message,
      response: error.response,
      contact: error.contact,
      config: error.config,
    })
    const message = error.response?.data || "Error desconocido al actualizar la solicitud"
    return {
      success: false,
      message,
    }
  }
}

const deleteContact = async (
  contactId: number,
  userProfileId: number
): Promise<{ success: boolean; message: string }> => {
  try {
    const url = `${API_URL}/contact/delete-contact/${contactId}/${userProfileId}`

    const response = await axios.delete(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
    })

    const message = response.data || "Contacto eliminado con éxito"
    contactStore.clear()
    cache.invalidateAll()

    return {
      success: true,
      message,
    }
  } catch (error: any) {
    console.error("Error en deleteContact:", error)
    console.error("Detalles:", {
      message: error.message,
      response: error.response,
      request: error.request,
      config: error.config,
    })
    const message = error.response?.data || "Error desconocido al eliminar el contacto"
    return {
      success: false,
      message,
    }
  }
}


const handleRequestError = (error: any) => {
  console.error("handleRequestError - error completo:", error)

  if (error.response) {
    console.error("Error de respuesta:", {
      status: error.response.status,
      data: error.response.data,
      headers: error.response.headers,
    })
    throw new Error(error.response.data || error.message)
  } else if (error.request) {
    console.error("Error de solicitud (no se recibió respuesta):", error.request)
    throw new Error("No se recibió respuesta del servidor")
  } else {
    console.error("Error de configuración:", error.message)
    throw new Error(error.message || "Error desconocido")
  }
}

export const contactService = {
  getUserContacts,
  getContactById,
  createContact,
  updateContact,
  deleteContact
}

export default contactService
